import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { useMfaConfirm, useMfaDisable, useMfaEnroll, useMfaRegenerateRecoveryCodes, useMfaStatus, } from '@/hooks/use-mfa';
import { useToast } from '@/hooks/use-toast';
import { saveBlob } from '@/lib/download/saveBlob';
import { Copy, ExternalLink, Loader2, ShieldCheck } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
/** "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP" (display only). */
function groupSecret(secret) {
    return secret.replace(/(.{4})(?=.)/g, '$1 ');
}
/**
 * Two-factor authentication panel for MyAccountModal. Three views:
 *  - status (enabled → password + disable/regenerate; disabled → enable button)
 *  - enrolling (QR + manual key + confirm code)
 *  - recovery codes (shown exactly once after confirm/regenerate)
 */
export function MfaSection() {
    const { t } = useTranslation();
    const { toast } = useToast();
    const status = useMfaStatus();
    const enroll = useMfaEnroll();
    const confirm = useMfaConfirm();
    const disable = useMfaDisable();
    const regenerate = useMfaRegenerateRecoveryCodes();
    const [enrollState, setEnrollState] = useState(null);
    const [confirmCode, setConfirmCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const handleEnroll = () => {
        enroll.mutate(undefined, {
            onSuccess: (data) => {
                setConfirmCode('');
                setEnrollState(data);
            },
        });
    };
    const handleConfirm = () => {
        confirm.mutate(confirmCode.trim(), {
            onSuccess: (data) => {
                setEnrollState(null);
                setRecoveryCodes(data.recoveryCodes);
                toast({ title: t('account.mfa.title'), description: t('account.mfa.enabledToast') });
            },
        });
    };
    const handleDisable = () => {
        disable.mutate(currentPassword, {
            onSuccess: () => setCurrentPassword(''),
        });
    };
    const handleRegenerate = () => {
        regenerate.mutate(currentPassword, {
            onSuccess: (data) => {
                setCurrentPassword('');
                setRecoveryCodes(data.recoveryCodes);
            },
        });
    };
    const handleCopyCodes = async () => {
        if (!recoveryCodes)
            return;
        try {
            await navigator.clipboard.writeText(recoveryCodes.join('\n'));
            toast({
                title: t('account.mfa.recoveryCodesTitle'),
                description: t('account.mfa.copiedCodes'),
            });
        }
        catch {
            // clipboard API can reject (insecure context / denied permission); the
            // codes are still on screen to copy manually, so just surface a hint.
            toast({
                title: t('account.mfa.errorTitle'),
                description: t('account.mfa.copyFailed'),
                variant: 'destructive',
            });
        }
    };
    const handleCopySecret = async () => {
        if (!enrollState)
            return;
        try {
            await navigator.clipboard.writeText(enrollState.secret);
            toast({ title: t('account.mfa.title'), description: t('account.mfa.copiedKey') });
        }
        catch {
            // Same fallback as the recovery codes: the key stays on screen to copy by hand.
            toast({
                title: t('account.mfa.errorTitle'),
                description: t('account.mfa.copyKeyFailed'),
                variant: 'destructive',
            });
        }
    };
    const handleDownloadCodes = () => {
        if (!recoveryCodes)
            return;
        // saveBlob defers URL revocation so the one-shot download can't be dropped
        // before the browser queues it (Firefox/Chromium race on sync revoke).
        const blob = new Blob([recoveryCodes.join('\n')], { type: 'text/plain' });
        saveBlob(blob, 'newlumen-recovery-codes.txt');
    };
    if (status.isPending) {
        return (_jsx("div", { className: "flex justify-center py-4", children: _jsx(Loader2, { className: "text-muted-foreground h-5 w-5 animate-spin" }) }));
    }
    if (status.isError)
        return null;
    // View: one-time recovery-codes display (after confirm or regenerate)
    if (recoveryCodes) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx("h3", { className: "text-sm font-medium", children: t('account.mfa.recoveryCodesTitle') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('account.mfa.recoveryCodesDesc') }), _jsx("div", { className: "bg-muted/50 grid grid-cols-2 gap-2 rounded-lg border p-4 font-mono text-sm", children: recoveryCodes.map((code) => (_jsx("span", { children: code }, code))) }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", size: "sm", onClick: handleCopyCodes, children: t('account.mfa.copyCodes') }), _jsx(Button, { variant: "outline", size: "sm", onClick: handleDownloadCodes, children: t('account.mfa.downloadCodes') }), _jsx(Button, { size: "sm", className: "ml-auto", onClick: () => setRecoveryCodes(null), children: t('account.mfa.done') })] })] }));
    }
    // View: enrollment in progress (QR + confirm)
    if (enrollState) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx("h3", { className: "text-sm font-medium", children: t('account.mfa.title') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('account.mfa.scanQr') }), _jsx("div", { className: "flex justify-center", "data-testid": "mfa-qr", children: _jsx("div", { className: "rounded-lg bg-white p-3", children: _jsx(QRCodeSVG, { value: enrollState.otpauthUri, size: 168 }) }) }), _jsxs("div", { className: "flex flex-col gap-2 sm:flex-row sm:justify-center", children: [_jsx(Button, { asChild: true, variant: "outline", size: "sm", children: _jsxs("a", { href: enrollState.otpauthUri, "data-testid": "mfa-open-in-app", children: [_jsx(ExternalLink, { className: "mr-2 h-4 w-4", "aria-hidden": true }), t('account.mfa.openInApp')] }) }), _jsxs(Button, { variant: "outline", size: "sm", onClick: handleCopySecret, "data-testid": "mfa-copy-key", children: [_jsx(Copy, { className: "mr-2 h-4 w-4", "aria-hidden": true }), t('account.mfa.copyKey')] })] }), _jsxs("p", { className: "text-muted-foreground text-center text-xs", children: [t('account.mfa.manualEntry'), ' ', _jsx("span", { className: "font-mono break-all select-all", "data-testid": "mfa-secret", children: groupSecret(enrollState.secret) })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "mfa-confirm-code", children: t('account.mfa.codeLabel') }), _jsx(Input, { id: "mfa-confirm-code", value: confirmCode, onChange: (e) => setConfirmCode(e.target.value), placeholder: t('account.mfa.codePlaceholder'), autoComplete: "one-time-code", inputMode: "numeric", "data-testid": "mfa-confirm-code-input" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", className: "flex-1", onClick: () => setEnrollState(null), disabled: confirm.isPending, children: t('account.mfa.cancel') }), _jsx(Button, { className: "flex-1", onClick: handleConfirm, disabled: !confirmCode.trim(), loading: confirm.isPending, loadingText: t('account.mfa.confirming'), "data-testid": "mfa-confirm-button", children: t('account.mfa.confirm') })] })] }));
    }
    // View: status — enabled
    if (status.data?.enabled) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsxs("h3", { className: "flex items-center gap-2 text-sm font-medium", children: [_jsx(ShieldCheck, { className: "h-4 w-4 text-green-500" }), t('account.mfa.title')] }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('account.mfa.enabledDesc') }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "mfa-current-password", children: t('account.mfa.currentPasswordLabel') }), _jsx(PasswordInput, { id: "mfa-current-password", value: currentPassword, onChange: (e) => setCurrentPassword(e.target.value), placeholder: t('account.mfa.currentPasswordPlaceholder'), autoComplete: "current-password", "data-testid": "mfa-password-input" })] }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { variant: "outline", className: "flex-1", onClick: handleRegenerate, disabled: !currentPassword, loading: regenerate.isPending, loadingText: t('account.mfa.regenerating'), "data-testid": "mfa-regenerate-button", children: t('account.mfa.regenerate') }), _jsx(Button, { variant: "destructive", className: "flex-1", onClick: handleDisable, disabled: !currentPassword, loading: disable.isPending, loadingText: t('account.mfa.disabling'), "data-testid": "mfa-disable-button", children: t('account.mfa.disable') })] })] }));
    }
    // View: status — disabled
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("h3", { className: "text-sm font-medium", children: t('account.mfa.title') }), _jsx("p", { className: "text-muted-foreground text-sm", children: t('account.mfa.disabledDesc') }), _jsx(Button, { variant: "outline", className: "w-full", onClick: handleEnroll, loading: enroll.isPending, loadingText: t('account.mfa.enrolling'), "data-testid": "mfa-enable-button", children: t('account.mfa.enable') })] }));
}
